/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import type { HostContext, Logger, PreSearchGetRequestJson, PreSearchPostRequestJson } from '../@types';
import Request, { HttpResponse } from '../utils/Request';
import getConfig from '../config';
import PerformanceUtils from '../utils/PerformanceUtils';
import { getErrorMessage } from '../utils/getErrorMessage';
import { getLogger } from '../utils/SearchLogger';
import SearchUtils from '../utils/SearchUtils';
const uuid = require('uuid/v1');

class PreSearchService {
	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	/**
	 * Retrieves the search json data from the Search Server.
	 * @param {object} searchRequestBody - the search request to perform
	 * @returns {object|Error} - search datarequest response object, or an Error object if an invalid endpoint name was specified
	 */
	fetchPreSearchData(
		searchJson: PreSearchPostRequestJson | PreSearchGetRequestJson,
		{ authenticated },
	): Record<string, any> {
		let result;
		if (navigator.onLine) {
			const endpointPath = authenticated ? '/search/recommendations' : '/unauth/recommendations';
			this.logger.log('PreSearch Service', 'Making call to fetch PreSearch Data Results');
			const isCDNEnabledFromFloodgate = SearchUtils.isFloodgateFeatureInTest(
				'ccs.cdn.recommendations',
				this.searchHostContext,
			);
			result = isCDNEnabledFromFloodgate
				? this._performPreSearchDataGetRequest(searchJson, endpointPath)
				: this._performPreSearchDataPostRequest(searchJson, endpointPath);
		} else {
			result = {
				data: [],
			};
		}

		return result;
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	static instance: PreSearchService;
	searchHostContext: HostContext;
	logger: Logger;

	constructor(searchHostContext: HostContext) {
		if (!PreSearchService.instance) {
			this.searchHostContext = searchHostContext;
			this.logger = getLogger();
			PreSearchService.instance = this;
		}
		return PreSearchService.instance;
	}

	/**
	 * Retrieves the search json data from the Search Server.
	 * @param {bodyData} json body data
	 * @returns {Object} learn data request response object
	 */
	async _performPreSearchDataPostRequest(
		bodyData: Record<string, any>,
		path: string,
	): Promise<Record<string, any> | Error> {
		try {
			const env = this.searchHostContext.environment;
			const hostname = getConfig(env, 'SEARCH_SERVER');
			const requestId = uuid();
			const headers = {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				'x-api-key': this.searchHostContext.hostInfo.apiKey,
				authorization: `Bearer ${this.searchHostContext.imsInfo.accessToken}`,
				'x-product': `${getConfig(env, 'APP_NAME')}/${getConfig(env, 'APP_VERSION')}`,
				'x-product-location': `${getConfig(env, 'APP_NAME')}.UniversalSearchService`,
				'x-request-id': requestId,
			};
			const postData = JSON.stringify(bodyData);
			const startTime = Date.now();
			const preSearchResponse = <HttpResponse<Record<string, any>>>(
				await Request.withTimeout(5000)
					.withHeaders(headers)
					.withPayload(postData)
					.send('POST', `https://${hostname}${path}`)
			);

			if (preSearchResponse.isError()) {
				const serverData = {
					serverResult: {
						url: `https://${hostname}${path}`,
						status: preSearchResponse.status(),
						message: preSearchResponse.statusText(),
						requestId,
						requestBody: bodyData,
						duration: Date.now() - startTime,
					},
					hasServerError: true,
				};
				this.logger.error(
					'PreSearch Service',
					'performPreSearchDataRequest Problem with PreSearch Data Service response',
					{
						server: serverData,
						performanceTiming: PerformanceUtils.getPerformanceTimingByName(preSearchResponse.requestUrl()),
					},
				);
				const preSearchResponseError = new Error(
					`Floodgate Service response error code: ${preSearchResponse.status()}`,
				);
				return preSearchResponseError;
			}

			return preSearchResponse.data();
		} catch (err) {
			this.logger.error('PreSearch Service', 'Presearch response Service error', getErrorMessage(err));
			const preSearchResponseError = new Error('Presearch response Service error');
			return preSearchResponseError;
		}
	}
	async _performPreSearchDataGetRequest(
		queryParams: Record<string, any>,
		path: string,
	): Promise<Record<string, any> | Error> {
		try {
			const env = this.searchHostContext.environment;
			const hostname = getConfig(env, 'SEARCH_SERVER_CDN');
			const requestId = uuid();
			const headers = {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				'x-api-key': this.searchHostContext.hostInfo.apiKey,
				authorization: `Bearer ${this.searchHostContext.imsInfo.accessToken}`,
				'x-product': `${getConfig(env, 'APP_NAME')}/${getConfig(env, 'APP_VERSION')}`,
				'x-product-location': `${getConfig(env, 'APP_NAME')}.UniversalSearchService`,
				'x-request-id': requestId,
			};
			const startTime = Date.now();
			const preSearchResponse = <HttpResponse<Record<string, any>>>(
				await Request.withTimeout(5000)
					.withHeaders(headers)
					.withQueryParams(queryParams)
					.send('GET', `https://${hostname}${path}`)
			);

			if (preSearchResponse.isError()) {
				const serverData = {
					serverResult: {
						url: `https://${hostname}${path}`,
						status: preSearchResponse.status(),
						message: preSearchResponse.statusText(),
						requestId,
						requestBody: queryParams,
						duration: Date.now() - startTime,
					},
					hasServerError: true,
				};
				this.logger.error(
					'PreSearch Service',
					'performPreSearchDataRequest Problem with PreSearch Data Service response',
					{
						server: serverData,
						performanceTiming: PerformanceUtils.getPerformanceTimingByName(preSearchResponse.requestUrl()),
					},
				);
				const preSearchResponseError = new Error(
					`Floodgate Service response error code: ${preSearchResponse.status()}`,
				);
				return preSearchResponseError;
			}

			return preSearchResponse.data();
		} catch (err) {
			this.logger.error('PreSearch Service', 'Presearch response Service error', getErrorMessage(err));
			const preSearchResponseError = new Error('Presearch response Service error');
			return preSearchResponseError;
		}
	}
}

export default PreSearchService;
